import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ActionHistoryTooltip from './ActionHistoryTooltip';
import './ActionHistory.css';

const ACTION_HISTORY_DISPLAY_LIMIT = 5; // Number of actions to display at once

const ACTION_TYPE_COLORS = {
  'strategem-requested': '#8B4513',
  'drone-deployed': '#4a90e2',
  'drone-charge-used': '#2c5aa0',
  'weapon-equipped': '#ff6b35',
  'weapon-effect': '#ff8c42',
  'order-provided': '#9b59b6',
  'general-interaction': '#e74c3c',
  'unit-deployed': '#ffaa00',
  'unit-attacked': '#ff4444',
  'unit-killed': '#ff8800',
  'unit-enhanced': '#00cc66'
};

function ActionHistory({ actionHistory, gameState, userId, opponentId, onHighlightUnits }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [animatingInIndex, setAnimatingInIndex] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [viewOffset, setViewOffset] = useState(0); // Offset for swipe navigation (0 = most recent)
  const prevHistoryRef = useRef([]);
  const containerRef = useRef(null);
  const swipeStartRef = useRef(null);
  const swipeDistanceRef = useRef(0);
  const handleMouseMoveRef = useRef(null);
  const handleMouseUpRef = useRef(null);
  
  // Detect touch device
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Helper function to extract all unit instanceIds from an action
  const extractUnitInstanceIds = (action) => {
    const instanceIds = new Set();
    
    // Extract from primary item (actionItem, unit, killer, attacker, enhancer, drone, weapon, source, order)
    const primaryItem = action.actionItem || action.unit || action.killer || action.attacker || 
                        action.enhancer || action.drone || action.weapon || action.source || action.order;
    if (primaryItem?.instanceId) {
      instanceIds.add(primaryItem.instanceId);
    }
    
    // Extract from target
    if (action.target?.instanceId) {
      instanceIds.add(action.target.instanceId);
    }
    
    // Extract from targets array
    if (action.targets && Array.isArray(action.targets)) {
      action.targets.forEach(target => {
        if (target?.instanceId) {
          instanceIds.add(target.instanceId);
        }
      });
    }
    
    return instanceIds;
  };
  
  // Helper function to check if a unit is alive (in playedOrders, not in completedOrders)
  const isUnitAlive = (instanceId, gameState) => {
    if (!instanceId || !gameState?.state) return false;
    
    // Normalize instanceId to string for comparison
    const normalizedInstanceId = String(instanceId).trim();
    if (!normalizedInstanceId) return false;
    
    const allPlayerIds = gameState.players || Object.keys(gameState.state);
    
    for (const playerId of allPlayerIds) {
      const player = gameState.state[playerId];
      if (!player) continue;
      
      // Check if unit is in completedOrders (dead)
      if (player.completedOrders && Array.isArray(player.completedOrders)) {
        const inCompleted = player.completedOrders.some(order => 
          order && order.instanceId && String(order.instanceId).trim() === normalizedInstanceId
        );
        if (inCompleted) return false;
      }
      
      // Check if unit is in playedOrders (alive on battlefield)
      if (player.playedOrders) {
        for (const zone of ['front', 'flank']) {
          if (player.playedOrders[zone] && Array.isArray(player.playedOrders[zone])) {
            const inPlayed = player.playedOrders[zone].some(order => 
              order && order.instanceId && String(order.instanceId).trim() === normalizedInstanceId
            );
            if (inPlayed) return true;
          }
        }
      }
    }
    
    return false;
  };
  
  // Update highlighted units when hover state changes
  useEffect(() => {
    if (!onHighlightUnits || !gameState) {
      if (hoveredIndex === null) {
        onHighlightUnits([]);
      }
      return;
    }
    
    if (hoveredIndex === null) {
      // Clear highlights when not hovering
      onHighlightUnits([]);
      return;
    }
    
    // Calculate displayed actions to get the correct action
    const totalActions = actionHistory.length;
    const maxOffset = Math.max(0, totalActions - ACTION_HISTORY_DISPLAY_LIMIT);
    const clampedOffset = Math.min(viewOffset, maxOffset);
    const startIndex = Math.max(0, totalActions - ACTION_HISTORY_DISPLAY_LIMIT - clampedOffset);
    const endIndex = totalActions - clampedOffset;
    const displayedActions = actionHistory.slice(startIndex, endIndex);
    
    const action = displayedActions[hoveredIndex];
    if (!action) {
      onHighlightUnits([]);
      return;
    }
    
    // Extract all unit instanceIds from the action
    const instanceIds = extractUnitInstanceIds(action);
    
    // Filter to only alive units and convert to array
    const aliveInstanceIds = [];
    instanceIds.forEach(instanceId => {
      if (isUnitAlive(instanceId, gameState)) {
        aliveInstanceIds.push(instanceId);
      }
    });
    
    // Debug: Log what we're highlighting (only in development and only when hoveredIndex changes)
    if (process.env.NODE_ENV === 'development' && (aliveInstanceIds.length > 0 || instanceIds.size > 0)) {
      // Only log when actually hovering (not on every render)
      if (hoveredIndex !== null) {
        console.log('ActionHistory: Highlighting check', {
          actionType: action.type,
          aliveInstanceIds,
          allInstanceIds: Array.from(instanceIds),
          hoveredIndex,
          actionKeys: Object.keys(action),
          primaryItem: action.actionItem || action.unit || action.killer || action.attacker || action.enhancer || action.drone || action.weapon || action.source || action.order,
          target: action.target,
          targets: action.targets
        });
      }
    }
    
    // Update highlights
    onHighlightUnits(aliveInstanceIds);
  }, [hoveredIndex, actionHistory, viewOffset, gameState, onHighlightUnits]);
  
  // Close tooltip when tapping outside on touch devices
  useEffect(() => {
    if (!isTouchDevice || hoveredIndex === null) return;
    
    const handleTouchOutside = (e) => {
      // Check if tap is outside the action history container and tooltip
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setHoveredIndex(null);
      }
    };
    
    // Add listener with a small delay to avoid immediately closing
    const timeoutId = setTimeout(() => {
      document.addEventListener('touchstart', handleTouchOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('touchstart', handleTouchOutside);
    };
  }, [isTouchDevice, hoveredIndex]);
  
  // Reset view offset to 0 when new actions are added (show most recent)
  useEffect(() => {
    if (actionHistory && actionHistory.length > prevHistoryRef.current.length) {
      // New actions added - reset to show most recent
      setViewOffset(0);
    }
  }, [actionHistory]);

  // ✅ DOM-LEVEL MONITORING: Ensure we never render more than display limit tiles in the DOM
  // This catches cases where multiple components or layout duplications might show more than limit
  useEffect(() => {
    if (!containerRef.current) return;
    
    const items = containerRef.current.querySelectorAll('.action-history-item');
    const domCount = items.length;
    
    if (domCount > ACTION_HISTORY_DISPLAY_LIMIT) {
      console.error(`❌ ActionHistory: DOM has ${domCount} .action-history-item elements (max: ${ACTION_HISTORY_DISPLAY_LIMIT}). This indicates duplicate rendering.`);
    }
  }, [actionHistory, viewOffset]);

  useEffect(() => {
    // Calculate displayed actions based on view offset
    const totalActions = actionHistory.length;
    const startIndex = Math.max(0, totalActions - ACTION_HISTORY_DISPLAY_LIMIT - viewOffset);
    const endIndex = totalActions - viewOffset;
    const displayedActions = actionHistory.slice(startIndex, endIndex);
    
    // Only animate if we're viewing the most recent actions (viewOffset === 0)
    if (viewOffset === 0) {
      // Check if a new action was added
      if (displayedActions.length > 0 && prevHistoryRef.current.length > 0) {
        const newestAction = displayedActions[displayedActions.length - 1];
        const prevNewestAction = prevHistoryRef.current[prevHistoryRef.current.length - 1];
        
        // If we have a new action (different timestamp or instanceId)
        const newestActionItem = newestAction?.actionItem || newestAction?.unit || newestAction?.killer || newestAction?.attacker || newestAction?.enhancer || newestAction?.drone || newestAction?.weapon;
        const prevNewestActionItem = prevNewestAction?.actionItem || prevNewestAction?.unit || prevNewestAction?.killer || prevNewestAction?.attacker || prevNewestAction?.enhancer || prevNewestAction?.drone || prevNewestAction?.weapon;
        if (newestAction && (!prevNewestAction || 
            newestAction.timestamp !== prevNewestAction.timestamp ||
            newestActionItem?.instanceId !== prevNewestActionItem?.instanceId)) {
          // Animate in the newest item (use index within displayed actions, which is always last)
          const newIndex = displayedActions.length - 1;
          setAnimatingInIndex(newIndex);
          setTimeout(() => setAnimatingInIndex(null), 300);
        }
      } else if (displayedActions.length > 0 && prevHistoryRef.current.length === 0) {
        // First action - animate it in
        setAnimatingInIndex(0);
        setTimeout(() => setAnimatingInIndex(null), 300);
      }
    }
    
    // Store displayed actions for next comparison
    prevHistoryRef.current = displayedActions;
  }, [actionHistory, viewOffset]);

  // Mouse drag handlers for desktop - setup in useEffect
  useEffect(() => {
    // Create stable references for event handlers
    handleMouseMoveRef.current = (e) => {
      if (!swipeStartRef.current) return;
      const deltaX = e.clientX - swipeStartRef.current.x;
      const deltaY = e.clientY - swipeStartRef.current.y;
      
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        swipeDistanceRef.current = deltaX;
      }
    };
    
    handleMouseUpRef.current = () => {
      if (!swipeStartRef.current) return;
      
      const swipeThreshold = 50;
      const swipeTime = Date.now() - swipeStartRef.current.time;
      const maxSwipeTime = 300;
      const totalActions = actionHistory?.length || 0;
      const maxOffset = Math.max(0, totalActions - ACTION_HISTORY_DISPLAY_LIMIT);
      
      if (Math.abs(swipeDistanceRef.current) > swipeThreshold && swipeTime < maxSwipeTime) {
        if (swipeDistanceRef.current > 0) {
          // Swipe right: go forward (toward more recent actions)
          setViewOffset(prev => Math.max(0, prev - ACTION_HISTORY_DISPLAY_LIMIT));
        } else {
          // Swipe left: go back (toward older actions)
          setViewOffset(prev => Math.min(maxOffset, prev + ACTION_HISTORY_DISPLAY_LIMIT));
        }
      }
      
      swipeStartRef.current = null;
      swipeDistanceRef.current = 0;
      if (handleMouseMoveRef.current) {
        document.removeEventListener('mousemove', handleMouseMoveRef.current);
      }
      if (handleMouseUpRef.current) {
        document.removeEventListener('mouseup', handleMouseUpRef.current);
      }
    };
    
    return () => {
      // Cleanup on unmount
      if (handleMouseMoveRef.current) {
        document.removeEventListener('mousemove', handleMouseMoveRef.current);
      }
      if (handleMouseUpRef.current) {
        document.removeEventListener('mouseup', handleMouseUpRef.current);
      }
    };
  }, [actionHistory?.length]);

  if (!actionHistory || actionHistory.length === 0) {
    return null;
  }

  // Calculate displayed actions based on view offset
  // viewOffset = 0: show most recent 5 actions
  // viewOffset = 5: show actions 6-10 from the end
  // viewOffset = 10: show actions 11-15 from the end, etc.
  const totalActions = actionHistory.length;
  const maxOffset = Math.max(0, totalActions - ACTION_HISTORY_DISPLAY_LIMIT);
  const clampedOffset = Math.min(viewOffset, maxOffset);
  
  const startIndex = Math.max(0, totalActions - ACTION_HISTORY_DISPLAY_LIMIT - clampedOffset);
  const endIndex = totalActions - clampedOffset;
  let displayedActions = actionHistory.slice(startIndex, endIndex);
  
  // Ensure we never display more than the display limit
  if (displayedActions.length > ACTION_HISTORY_DISPLAY_LIMIT) {
    displayedActions = displayedActions.slice(0, ACTION_HISTORY_DISPLAY_LIMIT);
  }
  
  // Calculate if we can swipe left (go back further) or right (go forward)
  const canSwipeLeft = clampedOffset < maxOffset;
  const canSwipeRight = clampedOffset > 0;
  
  // Debug: Log navigation state (only in development)
  if (process.env.NODE_ENV === 'development' && totalActions > ACTION_HISTORY_DISPLAY_LIMIT) {
    console.log('ActionHistory Navigation:', {
      totalActions,
      viewOffset,
      clampedOffset,
      maxOffset,
      canSwipeLeft,
      canSwipeRight,
      displayedCount: displayedActions.length
    });
  }
  
  // Swipe handlers
  const handleTouchStart = (e) => {
    if (!isTouchDevice) return;
    const touch = e.touches[0];
    swipeStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    swipeDistanceRef.current = 0;
  };
  
  const handleTouchMove = (e) => {
    if (!isTouchDevice || !swipeStartRef.current) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = touch.clientY - swipeStartRef.current.y;
    
    // Only track horizontal swipes (ignore vertical scrolling)
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      swipeDistanceRef.current = deltaX;
      e.preventDefault(); // Prevent scrolling while swiping
    }
  };
  
  const handleTouchEnd = (e) => {
    if (!isTouchDevice || !swipeStartRef.current) return;
    
    const swipeThreshold = 50; // Minimum distance for a swipe
    const swipeTime = Date.now() - swipeStartRef.current.time;
    const maxSwipeTime = 300; // Maximum time for a swipe (ms)
    const totalActions = actionHistory.length;
    const maxOffset = Math.max(0, totalActions - ACTION_HISTORY_DISPLAY_LIMIT);
    
    // Check if this was a valid swipe
    if (Math.abs(swipeDistanceRef.current) > swipeThreshold && swipeTime < maxSwipeTime) {
      if (swipeDistanceRef.current > 0) {
        // Swipe right: go forward (toward more recent actions)
        setViewOffset(prev => Math.max(0, prev - ACTION_HISTORY_DISPLAY_LIMIT));
      } else {
        // Swipe left: go back (toward older actions)
        setViewOffset(prev => Math.min(maxOffset, prev + ACTION_HISTORY_DISPLAY_LIMIT));
      }
    }
    
    swipeStartRef.current = null;
    swipeDistanceRef.current = 0;
  };
  
  const handleMouseDown = (e) => {
    // Don't start drag if clicking on navigation arrows or action items
    if (e.target.closest('.action-history-nav-indicator') || 
        e.target.closest('.action-history-item')) {
      return;
    }
    if (isTouchDevice) return;
    swipeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now()
    };
    swipeDistanceRef.current = 0;
    if (handleMouseMoveRef.current) {
      document.addEventListener('mousemove', handleMouseMoveRef.current);
    }
    if (handleMouseUpRef.current) {
      document.addEventListener('mouseup', handleMouseUpRef.current);
    }
  };
  
  // Click handlers for navigation arrows
  const handleNavLeftClick = (e) => {
    e.stopPropagation();
    if (canSwipeLeft) {
      const totalActions = actionHistory.length;
      const maxOffset = Math.max(0, totalActions - ACTION_HISTORY_DISPLAY_LIMIT);
      setViewOffset(prev => Math.min(maxOffset, prev + ACTION_HISTORY_DISPLAY_LIMIT));
    }
  };
  
  const handleNavRightClick = (e) => {
    e.stopPropagation();
    if (canSwipeRight) {
      setViewOffset(prev => Math.max(0, prev - ACTION_HISTORY_DISPLAY_LIMIT));
    }
  };
  
  return (
    <div 
      className="action-history-container" 
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseEnter={(e) => {
        // Only allow drag if not hovering over action items
        if (e.target.closest('.action-history-item')) {
          return;
        }
      }}
    >
      {/* Navigation indicators - clickable buttons */}
      {totalActions > ACTION_HISTORY_DISPLAY_LIMIT && canSwipeLeft && (
        <div 
          className="action-history-nav-indicator action-history-nav-left" 
          title={`Click or swipe left to see older actions (${totalActions - ACTION_HISTORY_DISPLAY_LIMIT - clampedOffset} more available)`}
          onClick={handleNavLeftClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleNavLeftClick(e);
            }
          }}
        >
          ←
        </div>
      )}
      {totalActions > ACTION_HISTORY_DISPLAY_LIMIT && canSwipeRight && (
        <div 
          className="action-history-nav-indicator action-history-nav-right" 
          title={`Click or swipe right to see newer actions (${clampedOffset} actions back)`}
          onClick={handleNavRightClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleNavRightClick(e);
            }
          }}
        >
          →
        </div>
      )}
      
      {/* Action items */}
      {displayedActions.map((action, index) => {
        const isAnimatingIn = animatingInIndex === index;

        // ✅ FIX: Build a more robust, collision-resistant React key
        // Include effectType, amount, target instanceId, and index so multiple
        // general interactions from the same source in the same millisecond
        // still get unique keys (prevents duplicate key warnings).
        const primaryItem =
          action.actionItem ||
          action.unit ||
          action.killer ||
          action.attacker ||
          action.enhancer ||
          action.drone ||
          action.weapon ||
          action.source ||
          action.order ||
          null;

        const primaryInstanceId = primaryItem?.instanceId || 'noInstanceId';
        const effectType = action.effect?.effectType || action.effectType || 'none';
        const amount =
          typeof action.amount !== 'undefined'
            ? action.amount
            : typeof action.effect?.amount !== 'undefined'
            ? action.effect.amount
            : 'na';
        const targetInstanceId =
          action.target?.instanceId ||
          (action.targets && action.targets[0]?.instanceId) ||
          'noTarget';
        const timestampPart = action.timestamp || 'noTimestamp';

        const actionKey = `${timestampPart}-${primaryInstanceId}-${action.type}-${effectType}-${amount}-${targetInstanceId}-${index}`;

        // Helper to calculate and show tooltip
        const showTooltip = (element) => {
          const rect = element.getBoundingClientRect();
          const tooltipCenterX = rect.left + window.scrollX + (rect.width / 2);
          
          // Estimate tooltip width - account for action item (80px) + gap (16px) + targets
          // Each target is ~88px (80px width + 8px gap), plus padding (24px total)
          const actionItemWidth = isTouchDevice ? 48 : 80; // Smaller on mobile
          const gap = isTouchDevice ? 8 : 16;
          const padding = isTouchDevice ? 16 : 24;
          const targetCount = displayedActions[index]?.targets?.length || (displayedActions[index]?.target ? 1 : 0);
          const targetsWidth = targetCount > 0 ? (targetCount * (isTouchDevice ? 56 : 88)) : 0;
          const estimatedTooltipWidth = actionItemWidth + gap + targetsWidth + padding;
          
          // Use a larger minimum left margin to ensure tooltip stays on screen
          const minLeftMargin = isTouchDevice ? 10 : 50;
          
          // Calculate left position to ensure tooltip stays on screen
          let tooltipLeft = tooltipCenterX;
          
          // If tooltip would extend off left edge, adjust it
          const tooltipLeftEdge = tooltipCenterX - (estimatedTooltipWidth / 2);
          if (tooltipLeftEdge < minLeftMargin) {
            tooltipLeft = minLeftMargin + (estimatedTooltipWidth / 2);
          }
          
          setTooltipPosition({
            top: rect.bottom + window.scrollY + 8,
            left: tooltipLeft
          });
        };

        return (
          <div
            key={actionKey}
            className={`action-history-item ${isAnimatingIn ? 'swoop-in' : ''} ${hoveredIndex === index ? 'hovered' : ''}`}
            // Touch devices: tap to toggle tooltip
            onClick={isTouchDevice ? (e) => {
              e.stopPropagation();
              if (hoveredIndex === index) {
                setHoveredIndex(null); // Tap again to close
              } else {
                setHoveredIndex(index);
                showTooltip(e.currentTarget);
              }
            } : undefined}
            // Desktop: hover behavior
            onMouseEnter={!isTouchDevice ? (e) => {
              e.stopPropagation(); // Prevent container from handling this
              setHoveredIndex(index);
              showTooltip(e.currentTarget);
            } : undefined}
            onMouseLeave={!isTouchDevice ? (e) => {
              e.stopPropagation(); // Prevent container from handling this
              setHoveredIndex(null);
            } : undefined}
            onMouseDown={(e) => {
              // Stop propagation to prevent container drag from interfering
              e.stopPropagation();
            }}
            style={{
              borderColor: ACTION_TYPE_COLORS[action.type] || '#666',
              zIndex: hoveredIndex === index ? 99998 : 'auto'
            }}
          >
            <div style={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: '2px', position: 'relative', zIndex: 0 }}>
              <img
                src={`/images/${(action.actionItem || action.unit || action.killer || action.attacker || action.enhancer || action.drone || action.weapon || action.source || action.order)?.id}.jpg`}
                alt={(action.actionItem || action.unit || action.killer || action.attacker || action.enhancer || action.drone || action.weapon || action.source || action.order)?.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>
        );
      })}
      {hoveredIndex !== null && displayedActions[hoveredIndex] && createPortal(
        <ActionHistoryTooltip
          action={displayedActions[hoveredIndex]}
          gameState={gameState}
          userId={userId}
          opponentId={opponentId}
          position={tooltipPosition}
        />,
        document.body
      )}
    </div>
  );
}

export default ActionHistory;

